import React, { useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { HooksDisplayGrid } from 'ui-components/components/hooks-display-grid';
import GridLoading from '../../../components/grid-loading';
import ErrorBoundary from '../../../components/error-boundary';
import { QuickSearchContext } from '../quicksearch-context';
import { useProductContext } from './product-context';
import baseColumns from '../columns';
import Global from '../../../store/global-selectors';
import { saveUserSettingGridConfig } from '../../../store/user/user.actions';
import { openProductDetailModal } from '../../../store/products/products.actions';
import EmptyGridPlaceholder from './grid-placeholder';
import style from '../../grid-page.module.less';
import { activeFilterViewId, statusViewId } from '../../../constants/products';
import { emptyArray } from 'ui-components/default-props/empty';

const getColumnId = ({ id }) => id;

const buildColumns = (columnConfig) =>
  columnConfig
    .map((col) => {
      const colToRender = baseColumns[col.mappedId] || baseColumns[col.id];
      if (colToRender) {
        return {
          ...colToRender,
          ...col,
          name: colToRender.name,
          render: colToRender.render,
        };
      }
      return null;
    })
    .filter((col) => !!col);

const ProductGrid = () => {
  const { isLoading: quickSearchLoading, quickSearch } = useContext(QuickSearchContext);
  const { entitiesLoading, productIds, setColumnSort, selectedProductIds, setSelectedProductIds, columnConfiguration } =
    useProductContext();
  const [expandedRows, setExpandedRows] = useState([]);
  const [gridColumns, setGridColumns] = useState([]);

  useEffect(() => {
    setGridColumns(buildColumns(columnConfiguration));
  }, [columnConfiguration]);

  const productEntities = useSelector((state) => Global.selectEntitiesProductsByIds(state, { productIds }));

  const isLoading = entitiesLoading || quickSearchLoading;

  const gridProducts = quickSearch.products || productEntities;
  const variantIds = useMemo(() => {
    return gridProducts.reduce((acc, product) => {
      if ((product?.productVariantIds?.length ?? 0) === 0) {
        return acc;
      }

      return [...acc, ...product.productVariantIds];
    }, []);
  }, [gridProducts]);

  const variantProducts = useSelector((state) => Global.selectEntitiesProductsByIds(state, { productIds: variantIds }));
  const gridData = useMemo(() => {
    return gridProducts.map((product) => {
      if ((product?.productVariantIds?.length ?? 0) === 0) {
        return { id: product.productId, ...product };
      }

      const variants = variantProducts.reduce((acc, variant) => {
        if (!product.productVariantIds.includes(variant.productId)) {
          return acc;
        }

        return [...acc, { id: variant.productId, ...variant }];
      }, []);

      return [product, ...variants];
    });
  }, [gridProducts, variantProducts]);

  const dispatch = useDispatch();
  const onColumnsChanged = useCallback(
    (columns) => {
      dispatch(saveUserSettingGridConfig('products', statusViewId, activeFilterViewId, columns));
      setGridColumns(buildColumns(columns));
    },
    [dispatch],
  );

  const onOrderByChanged = useCallback(
    (columns) => {
      dispatch(saveUserSettingGridConfig('products', statusViewId, activeFilterViewId, columns));
      const { id, sorted: sortDirection } = columns.find((col) => col.sorted);
      setColumnSort(id, sortDirection);
      setGridColumns(buildColumns(columns));
    },
    [dispatch, setColumnSort],
  );

  const onRowDoubleClick = useCallback(({ id }) => dispatch(openProductDetailModal(id)), [dispatch]);

  return (
    <div className={style.grid}>
      <ErrorBoundary errorMessageId='productsGrid'>
        <HooksDisplayGrid
          columns={gridColumns}
          data={!isLoading ? gridData : emptyArray}
          expandedRows={expandedRows}
          getColumnId={getColumnId}
          onColumnsChanged={onColumnsChanged}
          onExpandedRowsChanged={setExpandedRows}
          onOrderByChanged={onOrderByChanged}
          onRowDoubleClick={onRowDoubleClick}
          onSelectedChanged={setSelectedProductIds}
          placeholder={isLoading ? <GridLoading /> : <EmptyGridPlaceholder />}
          selected={selectedProductIds}
        />
      </ErrorBoundary>
    </div>
  );
};

export default ProductGrid;
